/*
Componente de interfaz gráfica para implementar el Nit y dígito de verificación. 
Recibe el valor del nit vía props.
Al especificar un Nit de 9 dígitos, calcula el dígito de verificación y lo muestra.
Si el documento es corto (RUT u otro), el DV puede digitarse manualmente.
El usuario puede editar el DV manualmente en cualquier momento.
Devuelve el Nit y dígito de verificación a través de la función onChange.

Props:
- valor: El NIT/documento
- digitoVerificacion o dv: El dígito de verificación
- noAutoCalcular: Si es true, NO calcula el DV automáticamente (útil para edición)
- onChange: Callback (nit, dv)
*/
import React from "react";
import { useState, useEffect, useRef } from "react";

function CampoNit(props) {

    const dvFromProps = () => {
        const raw = props.digitoVerificacion ?? props.dv;
        if (raw === undefined || raw === null) return undefined;
        return String(raw).trim();
    };

    const resolveDv = (nit, dvProp) => {
        // Si noAutoCalcular está activo, solo mostrar lo que viene de props
        if (props.noAutoCalcular) {
            // Solo mostrar DV si realmente existe en props (no undefined, no null, no vacío)
            if (dvProp !== undefined && dvProp !== null && dvProp !== '') {
                return String(dvProp);
            }
            return '';
        }
        
        // Comportamiento normal: calcular si es posible
        const calc = calcularDigitoVerificacion(nit);
        if (calc !== '') {
            return String(calc);
        }
        if (dvProp !== undefined) {
            return dvProp;
        }
        return '';
    };

    const [valorNit, setValorNit] = useState(() => String(props.valor ?? '').trim());
    const [digitoVerificacion, setDigitoVerificacion] = useState(() =>
        resolveDv(String(props.valor ?? '').trim(), dvFromProps())
    );
    // Referencia para saber si el usuario está editando manualmente el DV
    const dvEditadoManualmente = useRef(false);
    
    const inputClassName = `${props.inputClassName || 'form-control'}${props.error ? ' is-invalid' : ''}`;
    const dvBasisRem =
        props.dvBasisRem != null
            ? props.dvBasisRem
            : props.anchoDigito != null
              ? Math.max(3.25, Math.min(6, parseInt(props.anchoDigito, 10) / 10))
              : 4.5;

    useEffect(() => {
        const v = String(props.valor ?? '').trim();
        const dvProp = dvFromProps();
        setValorNit(v);
        // Solo auto-calcular si no se ha editado manualmente
        if (!dvEditadoManualmente.current) {
            setDigitoVerificacion(resolveDv(v, dvProp));
        }
    }, [props.valor, props.digitoVerificacion, props.dv]);

    const notifyChange = (nit, dv) => {
        if (typeof props.onChange === 'function') {
            props.onChange(nit, dv);
        }
    };

    const handleValorNit = (nit) => {
        setValorNit(nit);
        // Resetear bandera de edición manual al cambiar el NIT
        dvEditadoManualmente.current = false;
        setDigitoVerificacion((prev) => {
            // Si noAutoCalcular está activo, no calcular automáticamente
            if (props.noAutoCalcular) {
                notifyChange(nit, prev ?? '');
                return prev ?? '';
            }
            const calc = calcularDigitoVerificacion(nit);
            const next = calc !== '' ? String(calc) : (prev ?? '');
            notifyChange(nit, next);
            return next;
        });
    };

    const handleDigitoVerificacion = (e) => {
        const dv = e.target.value.trim().slice(0, 2);
        // Marcar que el usuario editó manualmente
        dvEditadoManualmente.current = true;
        setDigitoVerificacion(dv);
        notifyChange(valorNit, dv);
    };

    return (
        <div className="w-100">
            <div
                className="campo-nit-flex"
                style={{
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                }}
            >
                <div className="form-group campo-nit-main mb-0" style={{ flex: '1 1 0', minWidth: 0 }}>
                    <label htmlFor="numeroDocumento">NIT/Documento{props.requerido ? ' *' : ''}</label>
                    <input
                        type="text"
                        name="numeroDocumento"
                        className={inputClassName}
                        id="numeroDocumento"
                        value={valorNit}
                        onChange={(e) => handleValorNit(e.target.value)}
                        style={{ minWidth: 0, width: '100%' }}
                    />
                </div>
                <div
                    className="form-group campo-nit-dv mb-0"
                    style={{ flex: `0 0 ${dvBasisRem}rem`, minWidth: 0 }}
                >
                    <label htmlFor="digitoVerificacion">DV</label>
                    <input
                        type="text"
                        name="digitoVerificacion"
                        className={inputClassName}
                        id="digitoVerificacion"
                        value={digitoVerificacion ?? ''}
                        onChange={handleDigitoVerificacion}
                        style={{ textAlign: 'center', minWidth: 0, width: '100%' }}
                    />
                </div>
            </div>
            <div className="invalid-feedback visible">
                {props.error}
            </div>
        </div>
    );
}
export default CampoNit;


function calcularDigitoVerificacion(nit) {
    const s = String(nit ?? '').trim();
    if (s.length < 9) {
        return '';
    }
    const base = s.slice(0, 9);
    if (!/^\d{9}$/.test(base)) {
        return '';
    }

    let n1 = parseInt(base[0]);
    let n2 = parseInt(base[1]);
    let n3 = parseInt(base[2]);
    let n4 = parseInt(base[3]);
    let n5 = parseInt(base[4]);
    let n6 = parseInt(base[5]);
    let n7 = parseInt(base[6]);
    let n8 = parseInt(base[7]);
    let n9 = parseInt(base[8]);

    let sumatoria = n1 * 41 + n2 * 37 + n3 * 29 + n4 * 23 + n5 * 19 + n6 * 17 + n7 * 13 + n8 * 7 + n9 * 3;
    let mod = sumatoria % 11;
    let dv = mod;

    if (mod > 1) {
        dv = 11 - mod;
    }

    if (isNaN(dv)) {
        return '';
    }
    else {
        return dv;
    }
}
